import { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { createRepository } from '@/repositories/GenericRepository';
import PaginatedResponse from '@/types/paginatedResponse';
import { QueryParams } from '@/types/query';
import RepositoryResponse from '@/types/repositoryResponse';
import {
  MemberEligibilityCheckPayload,
  MemberEligibilityCheckResult,
  TreatmentGroup,
  TreatmentGroupDetailForPerson,
  TreatmentGroupForPerson,
  TreatmentGroupMembershipByPerson,
  TreatmentGroupPayload
} from '@/types/treatmentGroup';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

const basePath = '/api/treatment_groups';

export const treatmentGroupRepository = createRepository<TreatmentGroup, TreatmentGroupPayload>(
  basePath
);

const TreatmentGroupRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async list(params: QueryParams = {}) {
      return treatmentGroupRepository.list({ host, params });
    },

    async get(id: string) {
      return treatmentGroupRepository.get({ host, id });
    },

    async create(data: Partial<TreatmentGroupPayload>) {
      return treatmentGroupRepository.create({ host, data });
    },

    async update(id: string, data: Partial<TreatmentGroupPayload>) {
      return treatmentGroupRepository.update({ host, id, data });
    },

    async delete(id: string) {
      return treatmentGroupRepository.delete({ host, id });
    },

    async end(id: string, endDate: string) {
      const response = await ajax.patch<
        Partial<TreatmentGroupPayload>,
        AxiosResponse<TreatmentGroup>
      >({
        url: `${host}${basePath}/${id}/`,
        data: { end_date: endDate }
      });
      return response.data;
    },

    async checkMembers(data: MemberEligibilityCheckPayload) {
      const response = await ajax.post<
        MemberEligibilityCheckPayload,
        AxiosResponse<MemberEligibilityCheckResult>
      >({
        url: `${host}${basePath}/check_members/`,
        data
      });
      return response.data;
    },

    async getMembershipsByPerson(
      personId: string,
      excludeGroupId?: string
    ): Promise<TreatmentGroupMembershipByPerson[]> {
      const response = await ajax.get({
        url: `${host}${basePath}/memberships/`,
        params: { person_id: personId, exclude_group_id: excludeGroupId }
      });
      return response.data;
    },

    async listForPerson(
      personId: string,
      params: QueryParams = {}
    ): Promise<RepositoryResponse<PaginatedResponse<TreatmentGroupForPerson[]>>> {
      try {
        const response = await ajax.get({
          url: `${host}${basePath}/for_person/${personId}/`,
          params
        });
        return { data: response.data, isError: false };
      } catch (error) {
        logAjaxError(error);
        return defaultRepositoryError(
          error as AxiosError,
          `Failed to list ${basePath} for person.`
        );
      }
    },

    async getForPerson(
      groupId: string,
      personId: string,
      timezone?: string
    ): Promise<RepositoryResponse<TreatmentGroupDetailForPerson>> {
      try {
        const response = await ajax.get({
          url: `${host}${basePath}/${groupId}/for_person/${personId}/`,
          params: { timezone }
        });
        return { data: response.data, isError: false };
      } catch (error) {
        logAjaxError(error);
        return defaultRepositoryError(error as AxiosError, `Failed to get ${basePath} for person.`);
      }
    }
  };
};

export default TreatmentGroupRepository;
